import { useEffect, useRef, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import audioCopy from '@/i18n/ns/meeting-audio-input';
import audioStatusCopy from '@/i18n/ns/meeting-audio-status';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import {
  listAudioInputs,
  listKnownAudioInputs,
  type AudioInput,
} from '@/speech/capture/audio-inputs';
import { readMeetingAudioPreferences, revalidateAudioSelection } from './meeting-audio-preferences';

export { revalidateAudioSelection } from './meeting-audio-preferences';

export type MeetingAudioSelection = { systemDeviceId: string; microphoneDeviceId: string };
export const emptyAudioSelection = (): MeetingAudioSelection => ({
  systemDeviceId: '',
  microphoneDeviceId: '',
});
export const audioSelectionReady = (value: MeetingAudioSelection, both: boolean) =>
  Boolean(
    value.systemDeviceId &&
    (!both || (value.microphoneDeviceId && value.microphoneDeviceId !== value.systemDeviceId)),
  );

/** The browser exposes OS inputs; we never guess which one carries a call. */
export function MeetingAudioInput({
  both,
  value,
  onChange,
}: {
  both: boolean;
  value: MeetingAudioSelection;
  onChange: (value: MeetingAudioSelection) => void;
}) {
  const copy = useNamespaceCopy(audioCopy);
  const statusCopy = useNamespaceCopy(audioStatusCopy);
  const recordCopy = useNamespaceCopy(meetingsRecordCopy);
  const [inputs, setInputs] = useState<AudioInput[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const load = async (revalidate = false) => {
    setBusy(true);
    setError(false);
    if (!revalidate) onChange(emptyAudioSelection());
    try {
      const found = await (revalidate ? listKnownAudioInputs() : listAudioInputs());
      if (!mounted.current) return;
      setInputs(found);
      const remembered = revalidate ? readMeetingAudioPreferences()?.selection : null;
      const current = revalidate ? value : emptyAudioSelection();
      const next = remembered ?? current;
      const validated = revalidateAudioSelection(next, found);
      onChange(validated);
      setError(found.length === 0 || (Boolean(next.systemDeviceId) && !validated.systemDeviceId));
    } catch {
      if (mounted.current) setError(true);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  useEffect(() => {
    // A remembered monitor is useful only after this browser has re-enumerated
    // already exposed devices without requesting permission. If it disappeared,
    // leave the choice blank rather than silently
    // substituting another monitor.
    if (readMeetingAudioPreferences()) void load(true);
    // This effect intentionally runs once when the dialog mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="ws-meeting-audio-input">
      <p className="ws-page-note">
        {copy.guide} <a href="#/settings/personal/desktop">{statusCopy.downloadsFallback}</a>
      </p>
      <button type="button" className="ws-button" disabled={busy} onClick={() => void load()}>
        {busy ? recordCopy.starting : copy.load}
      </button>
      {error && <p role="alert">{copy.unavailable}</p>}
      {inputs.length > 0 && (
        <>
          <label>
            {copy.source}
            <select
              aria-label={copy.source}
              disabled={busy}
              value={value.systemDeviceId}
              onChange={(event) => onChange({ ...value, systemDeviceId: event.target.value })}
            >
              <option value="">{copy.choose}</option>
              {inputs.map((input) => (
                <option key={input.deviceId} value={input.deviceId}>
                  {input.label}
                </option>
              ))}
            </select>
          </label>
          {both && (
            <label>
              {copy.microphone}
              <select
                aria-label={copy.microphone}
                disabled={busy}
                value={value.microphoneDeviceId}
                onChange={(event) => onChange({ ...value, microphoneDeviceId: event.target.value })}
              >
                <option value="">{copy.choose}</option>
                {inputs
                  .filter((input) => input.deviceId !== value.systemDeviceId)
                  .map((input) => (
                    <option key={input.deviceId} value={input.deviceId}>
                      {input.label}
                    </option>
                  ))}
              </select>
            </label>
          )}
        </>
      )}
    </div>
  );
}
